<script setup lang="ts">
import type { SlotCapacityInfo } from '@/lib/types/slotCapacity';
import { useSlotDialog, type EditingSlot } from '@/composables/useSlotDialog';
import type { ReservationResourceCustomField } from '@/lib/types/reservation';
import type { Resource } from '@/lib/types/resource';
import { useResourcesStore } from '@/store/resource';
import { ceilToNext15Minutes, dateToTimeString, formatZoneOffsetLabel, isForeignTimezoneAt, msToResourceLocalTimeString, resourceLocalDateTimeToMs, roundToNearest15Minutes, timeStringToDate } from '@/utils/date';
import { computed, ref, watch, watchEffect } from 'vue';
import { useI18n } from 'vue-i18n';
import SlotTimelinePreview from './SlotTimelinePreview.vue';

const { t } = useI18n();
const resourcesStore = useResourcesStore();

interface Props {
    resource: Resource;
    selectedDate: Date | null;
    startTime: string | null;
    endTime: string | null;
    customFields: ReservationResourceCustomField[];
    capacityInfo: SlotCapacityInfo | null;
    editingReservationId: string | null;
}

const props = defineProps<Props>();

const emit = defineEmits<{
    'update:startTime': [time: string | null];
    'update:endTime': [time: string | null];
    'update:customFields': [fields: ReservationResourceCustomField[]];
    'update:hasValidationError': [hasError: boolean];
    remove: [];
}>();

const slotDialog = useSlotDialog();

// Dynamic refs for time pickers
const startTimePickerRef = ref<any>(null);
const endTimePickerRef = ref<any>(null);

// The resource's own clock (D-12 / TZ-02). The card already holds the whole Resource, so no new prop
// is needed; null when the stored id resolves to no served timezone, which every utils/date.ts helper
// reads as "fall back to browser-local" (D-09).
const resourceTimezoneId = computed(() => props.resource.timezone?.id ?? null);

// UI-SPEC E4 / D-09 degraded gate: the stored id matched NOTHING in the served list. The test is
// "the list has loaded AND the resolved entry is absent" — never "the list is empty". `timezones` is
// [] both BEFORE the fetch resolves and AFTER it fails, so an empty-list test would flash this amber
// warning on every resource on a cold cache. `timezonesLoaded` is set only on a successful fetch.
const isTimezoneUnresolved = computed(() => resourcesStore.timezonesLoaded && props.resource.timezone === null);

// IC-1's "relevant instant" for this surface, degrading stepwise as the form fills: the slot's own
// start once both the date and the start time are known, else noon on the resource's clock on the
// selected date, else now. Noon rather than midnight so the notice cannot flicker on a DST-transition
// date, and so a form with no time entered yet still resolves to one unambiguous instant.
const noticeInstantMs = computed(() => {
    if (props.selectedDate && props.startTime) {
        const slotStartMs = resourceLocalDateTimeToMs(resourceTimezoneId.value, props.selectedDate, props.startTime);
        if (slotStartMs !== null) return slotStartMs;
    }
    if (props.selectedDate) {
        const noonMs = resourceLocalDateTimeToMs(resourceTimezoneId.value, props.selectedDate, '12:00');
        if (noonMs !== null) return noonMs;
    }
    return Date.now();
});

// IC-1 / D-16 / D-17: the SAME single predicate the calendar chip and the hover preview card call —
// called here, never copied. It compares the two zones' offsets at that instant, and it returns false
// for a null or unresolvable zone, which is what makes this notice and the degraded warning above
// mutually exclusive (IC-5) without any second guard.
const showTimezoneNotice = computed(() => isForeignTimezoneAt(resourceTimezoneId.value, noticeInstantMs.value));

// The `{zone}` interpolation value: the IANA id plus the zone's offset at that same instant.
const zoneLabel = computed(() => formatZoneOffsetLabel(resourceTimezoneId.value, noticeInstantMs.value));

// Build a pseudo EditingSlot for reusing composable functions
const pseudoSlot = computed((): EditingSlot => ({
    resourceId: props.resource.id,
    startMs: 0,
    endMs: 0,
    customFields: props.customFields,
    startDate: props.selectedDate,
    startTime: props.startTime,
    endTime: props.endTime,
    timezoneId: props.resource.timezone?.id ?? null
}));

// Timeline data
const selectedDayAvailability = computed(() =>
    slotDialog.getSelectedDayAvailability(pseudoSlot.value, props.resource.id)
);

const timelineBounds = computed(() =>
    slotDialog.getTimelineBounds(selectedDayAvailability.value)
);

const hasAvailability = computed(() => selectedDayAvailability.value !== null);

const reservationBlocks = computed(() =>
    slotDialog.getTimelineReservationBlocks(
        pseudoSlot.value,
        props.resource.id,
        timelineBounds.value,
        props.editingReservationId
    )
);

const maintenanceBlocks = computed(() =>
    slotDialog.getTimelineMaintenanceBlocks(pseudoSlot.value, timelineBounds.value)
);

// Custom fields from resource definition
const resourceCustomFields = computed(() =>
    slotDialog.getCustomFields(props.resource.id)
);

// Duration. TZ-02: resolved on the resource's clock, so durationText and the time-order check below
// measure the same span the backend will persist — on a DST transition date those differ by an hour.
const computedStartDateTime = computed(() =>
    slotDialog.parseTimeString(props.selectedDate, props.startTime, resourceTimezoneId.value)
);
const computedEndDateTime = computed(() =>
    slotDialog.parseTimeString(props.selectedDate, props.endTime, resourceTimezoneId.value)
);
const durationText = computed(() => {
    if (!computedStartDateTime.value || !computedEndDateTime.value) return null;
    if (computedStartDateTime.value >= computedEndDateTime.value) return null;
    return slotDialog.calculateDuration(computedStartDateTime.value, computedEndDateTime.value);
});

// Inline validation.
//
// TZ-02: the block times below are now resource-local "HH:mm" strings (getTimelineReservationBlocks /
// getTimelineMaintenanceBlocks convert them), so re-parsing them on the browser's clock would silently
// misalign the overlap test against the entered time — the client would clear a booking the server
// rejects, or flag one it accepts. Both sides therefore pass resourceTimezoneId.
//
// The touching-windows test itself is UNCHANGED: `!(endMs <= blockStartMs || startMs >= blockEndMs)`
// still treats an end that exactly equals a block's start as no overlap. Both sides of the comparison
// simply moved onto the same new clock together.
const validation = computed(() => {
    const start = computedStartDateTime.value;
    const end = computedEndDateTime.value;

    const timeOrderInvalid = !!(start && end && start >= end);

    let overlapsReservation = false;
    let overlapsMaintenance = false;

    if (start && end && start < end) {
        const startMs = start.getTime();
        const endMs = end.getTime();

        overlapsReservation = reservationBlocks.value.some(block => {
            const blockStartMs = slotDialog.parseTimeString(props.selectedDate, block.startTime, resourceTimezoneId.value)?.getTime() || 0;
            const blockEndMs = slotDialog.parseTimeString(props.selectedDate, block.endTime, resourceTimezoneId.value)?.getTime() || 0;
            return !(endMs <= blockStartMs || startMs >= blockEndMs);
        });

        overlapsMaintenance = maintenanceBlocks.value.some(block => {
            // D-09: a partially-blocking maintenance lowers the ceiling for its window, it does not bar the
            // window. Exceeding that cap is reported by capacityInfo.hasError below, which mirrors what the
            // server actually enforces (ReservationService.ValidateCapacity). Same reasoning as
            // effectiveOverlapsReservation above: when the capacity system governs a window, a hard overlap
            // error rejects bookings the API accepts. Fully-blocking spans carry reducedCapacity null and keep
            // blocking — including those on capacity<=1 resources, which ExpandSlotToSpans degrades from
            // PartiallyBlocking to FullyBlocking server-side, so they arrive here with a null cap.
            if (block.reducedCapacity != null) return false;

            const blockStartMs = slotDialog.parseTimeString(props.selectedDate, block.startTime, resourceTimezoneId.value)?.getTime() || 0;
            const blockEndMs = slotDialog.parseTimeString(props.selectedDate, block.endTime, resourceTimezoneId.value)?.getTime() || 0;
            return !(endMs <= blockStartMs || startMs >= blockEndMs);
        });
    }

    return { timeOrderInvalid, overlapsReservation, overlapsMaintenance };
});

// Only treat overlapsReservation as an error when capacity is not managed (capacityInfo is null).
// When capacity IS managed, the capacity system handles concurrent bookings.
const effectiveOverlapsReservation = computed(() =>
    validation.value.overlapsReservation && !props.capacityInfo
);

const hasValidationError = computed(() =>
    validation.value.timeOrderInvalid ||
    effectiveOverlapsReservation.value ||
    validation.value.overlapsMaintenance ||
    (props.capacityInfo?.hasError && !props.capacityInfo?.loading)
);

// Capacity badge palette (UI-SPEC contract 4), evaluated in order. Loading and the D-10 neutral state both render
// grey: a green chip before any attendee count has been stated would read as "your booking fits" when nothing has
// been checked yet. Fixed Tailwind palette utilities with no `dark:` variant — the MarcaCorona preset's inverted
// dark ramp makes the surface-token text colours unreadable, so none is used here.
const capacityBadgeClass = computed(() => {
    const info = props.capacityInfo;
    if (!info) return '';
    if (info.loading) return 'bg-gray-100 text-gray-700';
    if (info.requested === 0) return 'bg-gray-100 text-gray-700';
    if (info.hasError) return 'bg-red-100 text-red-700';
    return 'bg-green-100 text-green-700';
});

// Notify parent of validation state changes
watchEffect(() => {
    emit('update:hasValidationError', hasValidationError.value);
});

// Fetch timeline data when date or resource changes
watch(
    () => [props.resource.id, props.selectedDate] as const,
    ([resourceId, date]) => {
        if (resourceId && date) {
            slotDialog.fetchResourceTimelineData(resourceId);
            // D-12: both fetches query the resource's day, not the browser's.
            slotDialog.fetchDateReservations(resourceId, date, resourceTimezoneId.value);
            slotDialog.fetchDateMaintenances(resourceId, date, resourceTimezoneId.value);
        }
    },
    { immediate: true }
);

// Time picker helpers
function confirmTimePicker(picker: any, applyValue: (timeStr: string) => void) {
    if (picker) {
        const hour = picker.currentHour ?? 0;
        const minute = picker.currentMinute ?? 0;
        const date = new Date();
        date.setHours(hour, minute, 0, 0);
        const rounded = roundToNearest15Minutes(date);
        applyValue(dateToTimeString(rounded));
        picker.overlayVisible = false;
    }
}

function handleTimePickerShow(currentValue: string | null | undefined, applyValue: (timeStr: string) => void) {
    if (!currentValue || !currentValue.trim()) {
        // TZ-02: the seed is an INSTANT ("now"), and the field it lands in is read back on the
        // RESOURCE's clock by resourceLocalDateTimeToMs — so the instant has to be projected onto
        // that clock, not the browser's. Offering a Rome operator "09:15" for a Tokyo resource meant
        // 09:15 in Tokyo, while the D-19 notice directly above this card said exactly that.
        //
        // This is NOT the symmetric timeStringToDate/dateToTimeString round-trip the comment on the
        // DatePicker warns against leaving alone: that one carries a bare "HH:mm" literal through and
        // back, whereas this one starts from a real instant. msToResourceLocalTimeString falls back to
        // browser-local for an unresolvable zone (D-09), reproducing today's behaviour there.
        const nowOnResourceClock = timeStringToDate(msToResourceLocalTimeString(resourceTimezoneId.value, Date.now()));
        applyValue(dateToTimeString(ceilToNext15Minutes(nowOnResourceClock)));
    }
}

// Custom fields
function getCustomFieldValue(customFieldId: string): boolean {
    const field = props.customFields.find(cf => cf.customFieldId === customFieldId);
    return field?.value ?? false;
}

function setCustomFieldValue(customFieldId: string, value: boolean) {
    const updated = [...props.customFields];
    const existingIndex = updated.findIndex(cf => cf.customFieldId === customFieldId);

    if (value) {
        if (existingIndex >= 0) {
            updated[existingIndex] = { ...updated[existingIndex], value: true };
        } else {
            updated.push({ customFieldId, value: true, notes: undefined });
        }
    } else {
        if (existingIndex >= 0) {
            updated.splice(existingIndex, 1);
        }
    }
    emit('update:customFields', updated);
}

function getCustomFieldNotes(customFieldId: string): string {
    const field = props.customFields.find(cf => cf.customFieldId === customFieldId);
    return field?.notes ?? '';
}

function setCustomFieldNotes(customFieldId: string, notes: string) {
    const updated = [...props.customFields];
    const existingIndex = updated.findIndex(cf => cf.customFieldId === customFieldId);
    if (existingIndex >= 0) {
        updated[existingIndex] = { ...updated[existingIndex], notes: notes || undefined };
        emit('update:customFields', updated);
    }
}
</script>

<template>
    <div class="overflow-hidden">

        <!-- Header -->
        <div class="flex items-center gap-3 px-4 py-3 border-b border-surface-200">
            <div class="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                :style="{ backgroundColor: ('#' + resource.color || '#8B5CF6') + '20' }">
                <div class="w-3 h-3 rounded-full" :style="{ backgroundColor: '#' + resource.color || '#8B5CF6' }"></div>
            </div>
            <div class="flex-1 min-w-0">
                <div class="flex items-center gap-1 min-w-0">
                    <span class="font-semibold block truncate">{{ resource.name }}</span>
                    <!--
                        IC-5 / D-09: icon only. This header is a single truncating line beside the space
                        name and there is no room for text; the full sentence lives in the amber banner
                        below. It annotates, it does not block — nothing here is gated on it.
                    -->
                    <i v-if="isTimezoneUnresolved" class="fa-solid fa-triangle-exclamation text-orange-500 shrink-0"
                        role="img" :aria-label="t('calendar.timezone.unknown')"></i>
                </div>
                <div v-if="resource.type.name" class="flex items-center gap-2 mt-0.5">
                    <span class="text-sm truncate">
                        {{ resource.type.name }}
                    </span>
                </div>
            </div>
            <Button icon="fa-solid fa-xmark" text rounded severity="danger" size="small"
                :aria-label="t('common.delete')" @click="emit('remove')" class="shrink-0" />
        </div>

        <!-- Body -->
        <div class="p-4 space-y-4">
            <!-- Timeline Preview -->
            <SlotTimelinePreview v-if="selectedDate" :resource-id="resource.id" :selected-date="selectedDate"
                :start-time="startTime" :end-time="endTime" :loading="slotDialog.timelineLoading.value"
                :has-availability="hasAvailability" :timeline-bounds="timelineBounds"
                :reservation-blocks="reservationBlocks" :maintenance-blocks="maintenanceBlocks"
                @update:start-time="emit('update:startTime', $event)"
                @update:end-time="emit('update:endTime', $event)" />

            <p v-else class="text-xs text-muted-color text-center py-1">
                {{ t('calendar.new.resourcesSlots.timeline.dateHint') }}
            </p>

            <!--
                Exactly one of the next two blocks renders, never both (IC-5). The exclusion is
                structural — v-if / v-else-if here, plus the shared predicate's own false-for-null
                return. No third guard is needed and none should be added.

                One line covers both the timeline above it and the time inputs below it, which is where
                an operator is about to type a time.
            -->
            <div v-if="isTimezoneUnresolved" class="p-3 rounded-lg bg-orange-50 border border-orange-200">
                <div class="flex items-center gap-2 text-orange-700">
                    <i class="fa-solid fa-triangle-exclamation" aria-hidden="true"></i>
                    <span>{{ t('calendar.timezone.unknownDetail') }}</span>
                </div>
            </div>
            <!--
                D-19 notice, in the timeline dragHint's idiom. Two deliberate departures from it: the
                icon class is the one this codebase already uses, and the span must NOT carry the
                clipping utility the hint uses — the interpolated zone sits at the end of the sentence
                and is exactly what an ellipsis would eat, so this line has to wrap. items-start rather
                than items-center keeps the icon on the first line when the sentence wraps.
            -->
            <div v-else-if="showTimezoneNotice" class="flex items-start gap-1 text-sm text-gray-500">
                <i class="fa-solid fa-info-circle" aria-hidden="true"></i>
                <span>{{ t('calendar.new.resourcesSlots.timezoneNotice', { zone: zoneLabel }) }}</span>
            </div>

            <!--
                Time pickers + badges row.

                Do NOT "fix" timeStringToDate / dateToTimeString below to be timezone-aware. They are a
                symmetric browser-local round-trip of a bare "HH:mm" literal: the string goes in, a
                throwaway Date carries it to the picker, and the same string comes back out. The round
                trip preserves the literal in any zone, so it is correct as written. The zone is applied
                once, downstream, where that "HH:mm" is resolved into an instant (parseTimeString).
            -->
            <div class="flex items-end gap-3 flex-wrap">
                <div class="flex flex-col gap-1 min-w-[110px]" style="flex: 1 1 110px;">
                    <label class="font-medium text-xs">
                        {{ t('calendar.new.resourcesSlots.addSlot.startTime') }}
                        <span class="text-red-500">*</span>
                    </label>
                    <DatePicker ref="startTimePickerRef" timeOnly :stepMinute="15"
                        :modelValue="timeStringToDate(startTime)"
                        @update:modelValue="(value: any) => emit('update:startTime', dateToTimeString(value as Date))"
                        @hide="() => {
                            const rounded = roundToNearest15Minutes(timeStringToDate(startTime));
                            emit('update:startTime', dateToTimeString(rounded));
                        }" @show="() => handleTimePickerShow(startTime, (v) => emit('update:startTime', v))"
                        class="w-full">
                        <template #footer>
                            <div class="flex justify-end pt-2 border-t">
                                <Button :label="t('common.apply')" size="small" severity="success"
                                    @click="confirmTimePicker(startTimePickerRef, (v) => emit('update:startTime', v))" />
                            </div>
                        </template>
                    </DatePicker>
                </div>

                <div class="flex flex-col gap-1 min-w-[110px]" style="flex: 1 1 110px;">
                    <label class="font-medium text-xs">
                        {{ t('calendar.new.resourcesSlots.addSlot.endTime') }}
                        <span class="text-red-500">*</span>
                    </label>
                    <DatePicker ref="endTimePickerRef" timeOnly :stepMinute="15" :modelValue="timeStringToDate(endTime)"
                        @update:modelValue="(value: any) => emit('update:endTime', dateToTimeString(value as Date))"
                        @hide="() => {
                            const rounded = roundToNearest15Minutes(timeStringToDate(endTime));
                            emit('update:endTime', dateToTimeString(rounded));
                        }" @show="() => handleTimePickerShow(endTime, (v) => emit('update:endTime', v))"
                        class="w-full">
                        <template #footer>
                            <div class="flex justify-end pt-2 border-t">
                                <Button :label="t('common.apply')" size="small" severity="success"
                                    @click="confirmTimePicker(endTimePickerRef, (v) => emit('update:endTime', v))" />
                            </div>
                        </template>
                    </DatePicker>
                </div>

                <!-- Duration + Capacity badges -->
                <div class="flex items-center gap-1.5 pb-1 flex-wrap">
                    <span v-if="durationText"
                        class="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700 whitespace-nowrap">
                        <i class="fa-regular fa-clock mr-1"></i>{{ durationText }}
                    </span>
                    <span v-if="capacityInfo"
                        class="px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 whitespace-nowrap"
                        :class="capacityBadgeClass"
                        :aria-label="t('calendar.new.capacity.badgeAria', { available: capacityInfo.available, total: capacityInfo.total })">
                        <i v-if="capacityInfo.loading" class="fa-solid fa-spinner fa-spin"></i>
                        <template v-else>
                            <i class="fa-solid fa-users"></i>
                            {{ capacityInfo.available }}/{{ capacityInfo.total }}
                        </template>
                    </span>
                </div>
            </div>

            <!-- Validation messages (compact) -->
            <div v-if="validation.timeOrderInvalid"
                class="flex items-center gap-2 text-red-600 text-xs p-2 bg-red-50 rounded-lg">
                <i class="fa-solid fa-triangle-exclamation"></i>
                <span>{{ t('calendar.new.resourcesSlots.addSlot.validation.endAfterStart') }}</span>
            </div>
            <div v-else-if="effectiveOverlapsReservation"
                class="flex items-center gap-2 text-red-600 text-xs p-2 bg-red-50 rounded-lg">
                <i class="fa-solid fa-calendar-xmark"></i>
                <span>{{ t('calendar.new.resourcesSlots.addSlot.validation.overlapsReservation') }}</span>
            </div>
            <div v-else-if="validation.overlapsMaintenance"
                class="flex items-center gap-2 text-orange-600 text-xs p-2 bg-orange-50 rounded-lg">
                <i class="fa-solid fa-wrench"></i>
                <span>{{ t('calendar.new.resourcesSlots.addSlot.validation.overlapsMaintenance') }}</span>
            </div>

            <!-- Capacity error -->
            <div v-if="capacityInfo?.hasError && !capacityInfo?.loading"
                class="flex items-center gap-2 text-red-600 text-xs p-2 bg-red-50 rounded-lg">
                <i class="fa-solid fa-triangle-exclamation"></i>
                <span>{{ capacityInfo.errorMessage }}</span>
            </div>

            <!-- Custom Fields -->
            <div v-if="resourceCustomFields.length > 0" class="border-t border-surface-200 pt-2">
                <div class="space-y-1.5 mt-2">
                    <div v-for="field in resourceCustomFields" :key="field.id"
                        class="flex items-start gap-2 p-2 rounded bg-surface-50">
                        <Checkbox :modelValue="getCustomFieldValue(field.id)"
                            @update:modelValue="(val: boolean) => setCustomFieldValue(field.id, val)" :binary="true"
                            :inputId="`cf-${resource.id}-${field.id}`" />
                        <div class="flex-1 min-w-0">
                            <label :for="`cf-${resource.id}-${field.id}`" class="cursor-pointer">
                                <span class="font-medium text-sm">{{ field.title }}</span>
                                <span v-if="field.description" class="text-xs text-gray-500 ml-1">
                                    {{ field.description }}
                                </span>
                            </label>
                            <InputText v-if="getCustomFieldValue(field.id)" :modelValue="getCustomFieldNotes(field.id)"
                                @update:modelValue="(val: any) => setCustomFieldNotes(field.id, val ?? '')"
                                class="w-full mt-1" size="small"
                                :placeholder="t('calendar.new.resourcesSlots.customFields.notesPlaceholder')" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
